import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { PhoneOff, Plus, Trash2, Upload } from 'lucide-react';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card } from '@/components/ui/Card';

interface DncEntry {
  phoneNumber: string;
  source: string;
  addedAt: string;
  expiresAt: string | null;
  note: string | null;
}

interface BulkResult {
  added: number;
  totalSubmitted: number;
}

/**
 * Story 51.4 — per-tenant Do-Not-Call list management. Backed by
 * /api/v1/dnc. The runtime gate lives in CallGuard; this page is the
 * compliance-officer-facing CRUD.
 */
export function DncListPage() {
  const { t } = useTranslation();
  const [entries, setEntries] = useState<DncEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshToken, setRefreshToken] = useState(0);
  const [bulkOpen, setBulkOpen] = useState(false);

  // Single-add form
  const [phoneNumber, setPhoneNumber] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    apiClient
      .get<DncEntry[]>('/dnc')
      .then(({ data }) => setEntries(data))
      .catch(() => setEntries([]))
      .finally(() => setLoading(false));
  }, [refreshToken]);

  async function handleAdd(e: FormEvent) {
    e.preventDefault();
    if (!phoneNumber.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      await apiClient.post('/dnc', {
        phoneNumber: phoneNumber.trim(),
        source: 'MANUAL',
        note: note.trim() || null,
      });
      setPhoneNumber('');
      setNote('');
      setRefreshToken((x) => x + 1);
    } catch (err) {
      const detail = isAxiosError(err)
        ? (err.response?.data as { detail?: string } | undefined)?.detail
        : null;
      setError(detail ?? t('dnc.error_add'));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleRemove(number: string) {
    try {
      await apiClient.delete('/dnc', { data: { phoneNumber: number } });
      setRefreshToken((x) => x + 1);
    } catch {
      setError(t('dnc.error_remove'));
    }
  }

  return (
    <div className="space-y-6 p-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <PhoneOff className="h-5 w-5 text-muted-foreground" />
          <div>
            <h1 className="text-2xl font-semibold">{t('dnc.title')}</h1>
            <p className="text-sm text-muted-foreground">{t('dnc.subtitle')}</p>
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={() => setBulkOpen(true)}>
          <Upload className="mr-1 h-4 w-4" /> {t('dnc.bulk_import')}
        </Button>
      </header>

      <Card>
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          {t('dnc.add_one')}
        </h2>
        <form onSubmit={handleAdd} className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <label className="text-xs font-medium">{t('dnc.field.phone')}</label>
            <Input
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder="+57 300 123 4567"
              className="w-64"
              required
            />
          </div>
          <div className="flex-1 space-y-1">
            <label className="text-xs font-medium">{t('dnc.field.note')}</label>
            <Input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t('dnc.note_placeholder')}
            />
          </div>
          <Button type="submit" size="sm" disabled={!phoneNumber.trim() || submitting}>
            <Plus className="mr-1 h-4 w-4" />
            {submitting ? t('dnc.adding') : t('dnc.add')}
          </Button>
        </form>
        {error && (
          <div className="mt-3 rounded-md border border-red-800 bg-red-950/40 px-3 py-2 text-sm text-red-300">
            {error}
          </div>
        )}
      </Card>

      <Card className="overflow-hidden p-0">
        <div className="border-b px-6 py-4">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {t('dnc.list_title')} ({entries.length})
          </h2>
        </div>
        {loading ? (
          <p className="px-6 py-6 text-sm text-muted-foreground">{t('common.loading')}</p>
        ) : entries.length === 0 ? (
          <p className="px-6 py-6 text-sm text-muted-foreground">{t('dnc.empty')}</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-muted text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-2 text-left">{t('dnc.field.phone')}</th>
                <th className="px-4 py-2 text-left">{t('dnc.field.source')}</th>
                <th className="px-4 py-2 text-left">{t('dnc.field.added_at')}</th>
                <th className="px-4 py-2 text-left">{t('dnc.field.expires_at')}</th>
                <th className="px-4 py-2 text-left">{t('dnc.field.note')}</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.phoneNumber} className="border-t">
                  <td className="px-4 py-2 font-mono text-xs">{entry.phoneNumber}</td>
                  <td className="px-4 py-2 text-xs text-muted-foreground">{entry.source}</td>
                  <td className="px-4 py-2 text-xs">
                    {entry.addedAt ? new Date(entry.addedAt).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-4 py-2 text-xs">
                    {entry.expiresAt ? new Date(entry.expiresAt).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-4 py-2 text-xs text-muted-foreground">{entry.note ?? '—'}</td>
                  <td className="px-4 py-2 text-right">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                      onClick={() => handleRemove(entry.phoneNumber)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {bulkOpen && (
        <BulkDncDialog
          onClose={() => setBulkOpen(false)}
          onImported={() => {
            setBulkOpen(false);
            setRefreshToken((x) => x + 1);
          }}
        />
      )}
    </div>
  );
}

function BulkDncDialog({
  onClose,
  onImported,
}: {
  onClose: () => void;
  onImported: () => void;
}) {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const [source, setSource] = useState('BULK_IMPORT');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<BulkResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const numbers = text
      .split(/[\n,;]+/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    if (numbers.length === 0) return;
    setSubmitting(true);
    setError(null);
    try {
      const { data } = await apiClient.post<BulkResult>('/dnc/bulk', {
        phoneNumbers: numbers,
        source: source.trim() || 'BULK_IMPORT',
      });
      setResult(data);
    } catch (err) {
      const detail = isAxiosError(err)
        ? (err.response?.data as { detail?: string } | undefined)?.detail
        : null;
      setError(detail ?? t('dnc.error_bulk'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-lg rounded-lg border bg-card p-6 shadow-xl">
        <h3 className="mb-4 text-lg font-semibold">{t('dnc.bulk_import_title')}</h3>
        {result ? (
          <div className="space-y-3">
            <p className="text-sm">
              {t('dnc.bulk_done', {
                added: result.added,
                total: result.totalSubmitted,
                defaultValue: '{{added}} of {{total}} added (the rest were duplicates).',
              })}
            </p>
            <div className="flex justify-end">
              <Button size="sm" onClick={onImported}>
                {t('common.save', { defaultValue: 'Done' })}
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="space-y-1">
              <label className="text-sm font-medium">{t('dnc.field.source')}</label>
              <Input
                value={source}
                onChange={(e) => setSource(e.target.value)}
                placeholder="REGULATORY_LIST"
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">{t('dnc.bulk_paste_label')}</label>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={8}
                className="w-full rounded-md border bg-transparent px-3 py-2 text-sm font-mono"
                placeholder={'+57 300 1234567\n+52 55 12345678\n+54 11 12345678'}
              />
              <p className="text-xs text-muted-foreground">{t('dnc.bulk_format_hint')}</p>
            </div>
            {error && (
              <div className="rounded-md border border-red-800 bg-red-950/40 px-3 py-2 text-sm text-red-300">
                {error}
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" size="sm" onClick={onClose}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" size="sm" disabled={!text.trim() || submitting}>
                {submitting ? t('dnc.adding') : t('dnc.bulk_submit')}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
